/**
 * The words for "how you get back tomorrow" (components/KeepAsk.tsx), asked
 * the moment the first print has been posted, and for carrying the road to the
 * phone's own browser (components/MoveToBrowser.tsx).
 *
 * Why there, and why this: on 2026-10-16 a visitor finished Day 1 inside
 * Facebook's browser, opened the app in Safari twenty seconds later, and met
 * the intro as somebody new. The two browsers keep nothing in common. Until
 * then the email was asked before the darkroom ("Where can we reach you?") and
 * one finisher in five gave it. Here it is asked after the reward, and says the
 * one thing it is for: getting back tomorrow.
 *
 * The first-screen rules hold (data/keep-copy.test.ts): no exclamation marks,
 * no handles, nothing on the banned list.
 */
import type { Platform } from '../lib/in-app-browser';

/** The browser a person would open, by the phone they are on. */
export function browserName(os: Platform): string {
  return os === 'ios' ? 'Safari' : os === 'android' ? 'Chrome' : 'your browser';
}

export const KEEP = {
  label: 'Day 2 opens tomorrow',
  title: 'How you get back tomorrow.',
  /** in a real browser */
  body: 'Add your email and we send you one link. It keeps your first creation, and it brings you back to your road tomorrow, on this phone or any other.',
  /** inside Facebook's or Instagram's browser: `app` is whose, `browser` the phone's own */
  inApp: (app: string, browser: string) => `${app}’s browser forgets. Add your email and we send you one link: it opens your road in ${browser}, kept, and that is where Day 2 waits tomorrow.`,
  placeholder: 'Your email',
  send: 'Send my link',
  waiting: 'One moment',
  skip: 'Not now',
  bad: 'That doesn’t look like an email yet.',
  /** inside an in-app browser, for somebody who would rather not give an email */
  moveInstead: (browser: string) => `Open it in ${browser} instead`,
  sentTitle: 'Your link is on its way.',
  sentBody: 'Tap it and your road is kept. Tomorrow, Kraben writes when Day 2 is ready.',
  sentInApp: (browser: string) => `Tap it and your road opens in ${browser}, kept. That is where Day 2 waits tomorrow.`,
  /** the email was kept on their doc but no link could be sent */
  keptTitle: 'We have your email.',
  /** standalone: when no code could be minted either, nothing follows it (MoveToBrowser shows only with one) */
  keptBody: 'The link did not go just now. Your road is still here, and you can try again.',
  other: 'Use a different email',
  again: 'Try again',
  done: 'Keep going',
  /**
   * Asked between the print on the line and its turn (lean Day 1, 2026-10-30):
   * the picture is right there, face-down. Send turns it over; so does "Not now".
   */
  turn: {
    label: 'It’s dry',
    title: 'Where should we keep it?',
    body: 'Add your email and we send you one link. It keeps this picture, and it brings you back to your road tomorrow, on this phone or any other.',
    skip: 'Not now, just turn it over',
    /** the way on once the email is in, inside Facebook's browser (or after Open it in Safari) */
    go: 'Turn it over',
  },
  /** on the road afterwards, once the email went in before the turn */
  roadSent: 'Your link is on its way. Tap it to keep your road.',
  roadKept: 'We have your email.',
};

export const MOVE = {
  lead: (browser: string) => `Or open it in ${browser} now:`,
  /** iOS: what to tap in Facebook's or Instagram's browser; the ⋯ is bold on the page */
  menu: ['Tap ', '⋯', ' at the top of the screen, then ', 'Open in external browser', '.'] as const,
  chrome: 'Open in Chrome',
  copy: 'Copy my link',
  copied: (browser: string) => `Copied. Paste it into ${browser}.`,
  /** the clipboard refused: the link itself, to copy by hand */
  copyByHand: 'Copy this into your browser:',
  minting: 'One moment',
};

/**
 * The move to the phone's own browser did not come through (screens/HandoffStuck.tsx):
 * the work is safe where it was made, and this browser has none of it yet. Says
 * where it is before offering anything else, because the old fallback (a fresh
 * intro, no word) read as "it is all gone".
 */
export const STUCK = {
  title: 'Your picture did not come across',
  why: 'It is safe in the app you came from. Go back there to carry on, or try again here.',
  retry: 'Try again',
  fresh: 'Start fresh here',
};
